import type { QueryClient, QueryKey } from '@tanstack/react-query';

import { nodeDetailQueryKeys } from 'api/data/hooks/nodeDetailQueryKeys';
import type { ApiNodeDetail, ApiNodeProperty } from 'api/data/projects/types';
import { isSystemProperty, toPropertyValue } from 'api/data/projects/types';

/** `ApiNodeProperty.value` is a primitive; arrays and objects cannot be patched into the DTO. */
function isPatchableValue(value: unknown): value is string | number | boolean | null {
  return value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/** The DTO omits empty properties, so an unknown key with an empty value is not news. */
function isEmptyPropertyValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function valuesEqual(newValue: string | number | boolean | null, cachedValue: unknown): boolean {
  return String(newValue ?? '') === String(toPropertyValue(cachedValue) ?? '');
}

interface NodeDetailSync {
  /** The cached object itself when nothing changed, so React Query skips the update. */
  data: ApiNodeDetail;
  needsRefetch: boolean;
}

function planNodeDetailSync(cached: ApiNodeDetail, properties: Record<string, unknown>): NodeDetailSync {
  // Incoming key casing does not always match the DTO's, so match on lowercased keys. Built lazily:
  // an update carrying only system properties (e.g. `_CREATED`/`_UPDATED`) never needs the lookup, so
  // there is no reason to index the whole cached bag for it.
  let cachedKeys: Map<string, string> | undefined;
  const resolveCachedKey = (key: string): string | undefined => {
    cachedKeys ??= new Map(Object.keys(cached.properties).map((cachedKey) => [cachedKey.toLowerCase(), cachedKey]));
    return cachedKeys.get(key.toLowerCase());
  };

  let patched: Record<string, ApiNodeProperty> | undefined;
  let needsRefetch = false;
  const matchedCachedKeys = new Set<string>();

  for (const [key, value] of Object.entries(properties)) {
    if (isSystemProperty(key)) continue;

    const cachedKey = resolveCachedKey(key);
    if (!cachedKey) {
      // A field the panel does not know about; only the GET can supply its metadata.
      if (!isEmptyPropertyValue(value)) needsRefetch = true;
      continue;
    }
    matchedCachedKeys.add(cachedKey);

    if (!isPatchableValue(value)) {
      needsRefetch = true;
      continue;
    }

    const cachedProperty = cached.properties[cachedKey];
    if (valuesEqual(value, cachedProperty.value)) continue;

    patched ??= { ...cached.properties };
    patched[cachedKey] = { ...cachedProperty, value };
  }

  // A cached key missing from this bag was removed from Neo4j for this view (the live bag, or live
  // with proposal overrides merged on). Drop it. Empty values still present in the bag are patched
  // above — this loop is about absence, not emptiness.
  for (const cachedKey of Object.keys(cached.properties)) {
    if (matchedCachedKeys.has(cachedKey)) continue;
    patched ??= { ...cached.properties };
    delete patched[cachedKey];
  }

  return { data: patched ? { ...cached, properties: patched } : cached, needsRefetch };
}

/** One cached copy of a node: the live view, or one of the open proposals. */
export interface NodeDetailVariant {
  queryKey: QueryKey;
  /** The proposal this copy shows, or `undefined` for the live view. */
  proposalId: string | undefined;
}

/** Every cached copy of one node — the live view plus one per open proposal. */
export function findNodeDetailVariants(
  queryClient: QueryClient,
  projectId: string | undefined,
  nodeId: string | undefined
): NodeDetailVariant[] {
  if (!projectId || !nodeId) return [];

  // Prefix matching is case-sensitive and the incoming ids need not match the casing of the
  // route-derived cache key, so compare the ids ourselves — normalizing the target ids once.
  const matchesNode = nodeDetailQueryKeys.nodeMatcher(projectId, nodeId);
  return queryClient
    .getQueryCache()
    .findAll({ queryKey: nodeDetailQueryKeys.all() })
    .map((query) => query.queryKey)
    .filter(matchesNode)
    .map((queryKey) => ({ queryKey, proposalId: nodeDetailQueryKeys.proposalIdOf(queryKey) }));
}

/**
 * Bring one cached copy of a node up to date with the property bag that copy should show.
 *
 * A variant with no mounted observer isn't shown to anyone right now, so there is nothing to patch —
 * just mark it stale and let the next mount's GET bring it up to date. Only a variant currently
 * rendered gets the full reconciliation: overlapping properties patched in place (no refetch); when
 * the bag carries a property the cache cannot represent — one absent from the DTO, or a value that is
 * not a primitive — the query is invalidated so the GET can return proper field metadata (type,
 * isPrimary, etc.). Unknown keys with empty values are ignored: empty properties are omitted from the
 * DTO, so they are not news. A cached key the bag no longer carries at all was cleared elsewhere in
 * Neo4j, so it is dropped from the cache.
 */
export function syncNodeDetailVariant(
  queryClient: QueryClient,
  { queryKey }: NodeDetailVariant,
  properties: Record<string, unknown>
) {
  const cached = queryClient.getQueryData<ApiNodeDetail>(queryKey);
  if (!cached) return;

  if (!queryClient.getQueryCache().find({ queryKey })?.isActive()) {
    queryClient.invalidateQueries({ queryKey });
    return;
  }

  const { data, needsRefetch } = planNodeDetailSync(cached, properties);

  // Patch before invalidating so known properties update instantly instead of waiting for the GET.
  if (data !== cached) queryClient.setQueryData<ApiNodeDetail>(queryKey, data);

  if (needsRefetch) {
    queryClient.invalidateQueries({ queryKey });
  }
}
